import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";

import { DispositionChip } from "~/components/intake/DispositionChip";
import { StageChip } from "~/components/intake/StageChip";
import { TierBadge } from "~/components/intake/TierBadge";
import { taskClock, type TaskClock } from "~/lib/taskClock";
import { useNow } from "~/lib/useNow";
import {
  Card,
  KpiTile,
  TimerChip,
  type CardAccent,
} from "~/components";
import {
  boardCasesQueryOptions,
  isDeadEnd,
  STAGES,
  STAGE_WAIT_TASKS,
  WAIT_TITLES,
  FIRST_CONTACT_TASK_TYPE,
  SIGN_RETAINER_TASK_TYPE,
  type Case,
  type Stage,
} from "~/lib/cases";
import { formatPhone } from "~/lib/phones";
import { caseTypeLabel, caseTypeOf } from "~/lib/triage";
import { tasksListQueryOptions, type Task } from "~/lib/tasks";
import { useOpenMatter } from "~/lib/threads";
import { queryClient } from "~/lib/queryClient";

import * as css from "~/routes/_app.alpha.index.css";

// Tower: the live board, and the workspace's nothing-focused state
// (DESIGN.md §5): every matter, every source, one screen. Click one and
// you're working it. Cards appear when the signals channel says the
// collection changed. They ease in where they belong, no sound, no
// toast, no navigation (DESIGN.md §9).
//
// A card's column is the matter's OWN `stage` column, projected off its plan by
// the API (TMF-360), so the board groups without pulling a project and its
// task rows down per row. What it still needs rows for is the two clocks, and
// it reads those as task collections filtered by type and joins them to the
// cards on `project_id`. A card's name is the matter's own `name` column and
// the caller's number rides the one embed a matter list carries (TMF-512), so
// the board fetches no people of its own.
export const Route = createFileRoute("/_app/alpha/")({
  loader: async () => {
    await queryClient.ensureQueryData(boardCasesQueryOptions);
  },
  component: TowerBoard,
});

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

/** How many deadline rows one page of the board reads. The two clocks are
 *  firm-wide task reads, so they are paged like the collections beside
 *  them. */
const TIMER_PAGE_SIZE = 200;

/** The speed-to-lead rows still open: the clock this product exists to stop. */
const firstContactTasksQueryOptions = tasksListQueryOptions({
  type: FIRST_CONTACT_TASK_TYPE,
  state: ["todo", "in progress"],
  size: TIMER_PAGE_SIZE,
});

/** The agreements still out: the unsigned clock a Retainer Sent card shows. */
const unsignedRetainerTasksQueryOptions = tasksListQueryOptions({
  type: SIGN_RETAINER_TASK_TYPE,
  state: ["todo", "in progress"],
  size: TIMER_PAGE_SIZE,
});

/** The signatures that landed, which is what the "Signed this week" tile
 *  counts: signing is the PLAN's statement, and the completion carries the
 *  moment it happened. */
const signedRetainerTasksQueryOptions = tasksListQueryOptions({
  type: SIGN_RETAINER_TASK_TYPE,
  state: ["complete"],
  size: TIMER_PAGE_SIZE,
});

/** A matter with everything the board derived for it, computed once per render
 *  and passed down, so no card re-derives what its column already knows.
 *
 *  The KPI tiles read `firstContactClock` rather than `clock`: speed to lead
 *  stays a count of FIRST-CONTACT breaches whatever else the board is timing. */
interface BoardCase {
  matter: Case;
  stage: Stage;
  /** The wait this matter's stage is on, and its tooltip. Null when the stage
   *  waits on nothing or the row is not open. */
  wait: { task: Task; title: string } | null;
  clock: TaskClock;
  firstContactClock: TaskClock;
  /** When the retainer was signed, for the weekly tile. Null unless a signed
   *  row says so. */
  signedAt: string | null;
}

/** The task rows of one type, keyed by the project they belong to. A matter's
 *  list row carries its project id, which is the whole join. */
function byProject(tasks: Task[]): Map<string, Task> {
  const map = new Map<string, Task>();
  for (const task of tasks) map.set(task.project_id, task);
  return map;
}

function TowerBoard() {
  const { data: caseCol } = useSuspenseQuery(boardCasesQueryOptions);
  const { data: firstContactCol } = useSuspenseQuery(firstContactTasksQueryOptions);
  const { data: unsignedCol } = useSuspenseQuery(unsignedRetainerTasksQueryOptions);
  const { data: signedCol } = useSuspenseQuery(signedRetainerTasksQueryOptions);
  const now = useNow();
  const { open, opening } = useOpenMatter();

  const matters = caseCol._embedded ?? [];

  const boardCases = useMemo<BoardCase[]>(() => {
    const firstContactTasks = byProject(firstContactCol._embedded ?? []);
    const unsignedTasks = byProject(unsignedCol._embedded ?? []);
    const signedTasks = byProject(signedCol._embedded ?? []);

    return matters.map((matter) => {
      // The list is fetched by stage, so every row has one; a matter without a
      // funnel is not on this board at all.
      const stage = matter.stage ?? "New";
      const waitType = STAGE_WAIT_TASKS[stage];
      const waitTask =
        waitType === FIRST_CONTACT_TASK_TYPE
          ? firstContactTasks.get(matter.project_id)
          : waitType === SIGN_RETAINER_TASK_TYPE
            ? unsignedTasks.get(matter.project_id)
            : undefined;
      const firstContactTask = firstContactTasks.get(matter.project_id) ?? null;
      return {
        matter,
        stage,
        wait:
          waitTask === undefined || waitType === null
            ? null
            : { task: waitTask, title: WAIT_TITLES[waitType] },
        clock: taskClock(waitTask ?? null, now),
        firstContactClock: taskClock(firstContactTask, now),
        signedAt: signedTasks.get(matter.project_id)?.completed_at ?? null,
      };
    });
  }, [matters, firstContactCol, unsignedCol, signedCol, now]);

  // One pass over the matters, bucketed by the stage the API projected, so a
  // tile can never disagree with the column beneath it. Every column the board
  // draws gets a bucket, including the empty ones.
  const columns = useMemo(() => {
    const byStage = new Map<Stage, BoardCase[]>(STAGES.map((stage) => [stage, []]));
    for (const entry of boardCases) {
      byStage.get(entry.stage)?.push(entry);
    }
    return byStage;
  }, [boardCases]);

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const weekAgo = now - MS_PER_WEEK;

  const newToday = matters.filter((m) => new Date(m.created_at) >= startOfToday).length;
  const live = boardCases.filter((e) => !isDeadEnd(e.stage));
  const awaiting = live.filter((e) => e.firstContactClock.running).length;
  const breaches = live.filter(
    (e) => e.firstContactClock.running && e.firstContactClock.level === "breach",
  ).length;
  const signedThisWeek = boardCases.filter((e) => {
    const signedAt = e.signedAt;
    return signedAt !== null && new Date(signedAt).getTime() >= weekAgo;
  }).length;

  return (
    <div className={css.board}>
      <div className={css.heading}>
        <h1 className={css.title}>Tower</h1>
        <span className={css.subtitle}>Every matter, every source, one screen.</span>
      </div>

      <div className={css.kpiStrip}>
        <KpiTile value={newToday} label="New today" />
        <KpiTile value={awaiting} label="Awaiting first touch" />
        <KpiTile value={breaches} label="Breaches" />
        <KpiTile value={signedThisWeek} label="Signed this week" gold />
      </div>

      <div className={css.columns}>
        {STAGES.map((stage) => (
          <BoardColumn
            key={stage}
            name={stage}
            entries={columns.get(stage) ?? []}
            onOpen={open}
            opening={opening}
          />
        ))}
      </div>
    </div>
  );
}

function BoardColumn({
  name,
  entries,
  onOpen,
  opening,
}: {
  name: Stage;
  entries: BoardCase[];
  onOpen: (matter: Case) => void;
  opening: boolean;
}) {
  // A column is a plain container (no role, and its only label is the stage
  // chip inside its header), so it carries a test handle keyed by the stage
  // it holds. `data-stage` is the key rather than a name spliced into the
  // testid: stage names contain spaces.
  return (
    <div className={css.column} data-testid="tower-column" data-stage={name}>
      <div className={css.columnHeader}>
        <StageChip name={name} />
        <span className={css.columnCount}>{entries.length}</span>
      </div>
      <div className={css.columnBody}>
        {entries.length === 0 ? (
          <span className={css.emptyColumn}>No matters</span>
        ) : (
          entries.map((entry) => (
            <BoardCard
              key={entry.matter.id}
              entry={entry}
              onOpen={onOpen}
              opening={opening}
            />
          ))
        )}
      </div>
    </div>
  );
}

function BoardCard({
  entry,
  onOpen,
  opening,
}: {
  entry: BoardCase;
  onOpen: (matter: Case) => void;
  opening: boolean;
}) {
  const { matter, clock, stage } = entry;
  const wait = clock.running ? entry.wait : null;
  // Null until the matter has been triaged: an untriaged card shows no tier at
  // all rather than a misleading C. Server-stamped, never derived here.
  const tier = matter.triage_tier;
  const settled = isDeadEnd(stage);
  const caseType = caseTypeOf(matter.triage);

  // Left accent only when the card is shouting about something real: the
  // stage's own clock past its thresholds (§6, one accent per card). The chip
  // inside the card reads the same ladder, so the two can never disagree.
  const accent: CardAccent = !clock.running
    ? "neutral"
    : clock.level === "breach"
      ? "danger"
      : clock.level === "warning"
        ? "warning"
        : "neutral";

  return (
    // The card IS a button and its accessible name is the person's name, so it
    // is reachable semantically; the handle exists only so a test can COUNT
    // the cards in a column, which no role query can express.
    <button
      type="button"
      className={css.cardButton}
      data-testid="tower-card"
      onClick={() => onOpen(matter)}
      disabled={opening}
    >
      <Card density="dense" accent={accent}>
        <div className={css.cardBody}>
          <span className={css.cardName}>{matter.name}</span>
          <span className={css.cardMeta}>
            <span className={css.cardMetaStart}>
              {formatPhone(matter._embedded?.contact?.phone)}
            </span>
            {caseType !== null ? (
              <span className={css.cardMetaEnd} data-testid="case-type" title={caseType}>
                {caseTypeLabel(caseType)}
              </span>
            ) : null}
          </span>
          <span className={css.cardMeta}>
            {matter.source !== null ? (
              <span className={css.cardMetaStart}>{matter.source}</span>
            ) : null}
            {matter.marketing_source !== null ? (
              <span className={css.cardMetaEnd} data-testid="marketing-source">
                {matter.marketing_source}
              </span>
            ) : null}
          </span>
          {wait !== null || tier !== null || settled ? (
            <span className={css.cardChips}>
              {wait !== null ? (
                <TimerChip
                  elapsedMs={clock.elapsedMs}
                  warnMs={clock.warnMs}
                  breachMs={clock.breachMs}
                  title={wait.title}
                />
              ) : null}
              {tier !== null ? <TierBadge tier={tier} /> : null}
              {settled ? <DispositionChip disposition={matter.disposition} /> : null}
            </span>
          ) : null}
        </div>
      </Card>
    </button>
  );
}
